import { POSConfigErrors } from "@/constants/errors/pos-config";
import { IPOSConfig } from "@/types/db/pos-config";
import { POSConfig } from "@/types/gql/PosConfigs";
import storage, { LocalStorageKeys } from "@/services/storage.service";
import POSConfigModule from "@/store/modules/pos-config";
import CheckInModule from "@/store/modules/check-in";
import {
  fetchConfigPCs,
  fetchConfigPicture,
  fetchConfigTimeslots,
} from "@/utils/vue-helpers/pos-config";
import { fetchConfigSeatingPlanParentPCs } from "@/utils/helpers/product-category";
import { fetchNonReccuringSeatingPlanTimeslots } from "@/utils/helpers/timeslots";
import { pullAndStorePromocodeCampaigns } from "@/utils/vue-helpers/promocode";
import { loadOffers } from "@/utils/helpers/offers";
import { syncGroupLocations } from "@/utils/helpers/device";
import { LogService } from "@/services/logging";
import NotificationService from "@/services/notification.service";

export function toLocalPOSConfig(posConfig: POSConfig): IPOSConfig {
  const {
    id,
    users,
    groupId,
    name,
    description,
    ticketGroups,
    gridsInfo,
    isRefundActivated,
  } = posConfig;

  if (
    !id ||
    !users ||
    !groupId ||
    !name ||
    !description ||
    !ticketGroups // ||
    // !gridsInfo
  ) {
    throw new Error(POSConfigErrors.MissingRequiredFields);
  }

  return {
    id,
    name,
    description,
    users:
      users && users.endsWith("]")
        ? (JSON.parse(users) as number[]) || []
        : users.split(",").map((user) => Number(user)),
    ticketGroups:
      ticketGroups && ticketGroups.endsWith("]")
        ? (JSON.parse(ticketGroups) as number[]) || []
        : ticketGroups.split(",").map((user) => Number(user)),
    group: groupId,
    gridsInfo: gridsInfo,
    isRefundActivated: !!isRefundActivated,
  };
}

export function setLocalStoragePOSConfig(storageData: string) {
  storage.setLocalStorageItem(LocalStorageKeys.PosConfig, storageData);
}

export function getLocalStoragePOSConfig() {
  return storage.getLocalStorageItem(LocalStorageKeys.PosConfig);
}

export function getJsonLocalStoragePOSConfig() {
  const config = getLocalStoragePOSConfig();
  return config ? JSON.parse(config) : "";
}

export function removeLocalStoragePOSConfig() {
  return storage.removeLocalStorageItem(LocalStorageKeys.PosConfig);
}

/**
 * Loads POS config data from remote and stores it locally.
 * This is the shared logic used both when selecting a new config
 * and when reloading the current config.
 *
 * @param config - The POS config to load data for
 * @returns true if the config was loaded successfully, false if the user lacks permission
 */
export async function loadConfigData(
  config: IPOSConfig,
  signal?: AbortSignal
): Promise<boolean> {
  if (!POSConfigModule.getPosConfigPermission) {
    // user is not allowed to sell using selected config
    POSConfigModule.removePOSConfig();
    NotificationService.showPosConfigurationError(config.name);
    return false;
  }

  // Reset extra progress
  POSConfigModule.setExtraProgress(0);

  // fetch product categories of the selected config from remote db and store them locally
  // PCs and timeslots are sequential (timeslots depend on PCs) and critical — let errors propagate
  POSConfigModule.setLoadingText("loading-products");
  await fetchConfigPCs(config, signal);
  if (signal?.aborted) return false;

  // fetch timeslots for the selected config
  POSConfigModule.setLoadingText("loading-timeslots");
  await fetchConfigTimeslots(config, signal);
  if (signal?.aborted) return false;

  // The remaining steps are independent of each other.
  // Each is wrapped in try/catch so a failure in one doesn't block the others.

  // Fetch seating plan parent PCs and their non-recurring timeslots
  POSConfigModule.setLoadingText("loading-seating-plans");
  if (!signal?.aborted) {
    try {
      await fetchConfigSeatingPlanParentPCs();
      await fetchNonReccuringSeatingPlanTimeslots();
    } catch (error) {
      LogService.info("Failed to fetch seating plan data", { error });
    }
  }
  POSConfigModule.setExtraProgress(1);

  // pull promocode campaigns and config picture
  POSConfigModule.setLoadingText("loading-promotions");
  if (!signal?.aborted) {
    try {
      await pullAndStorePromocodeCampaigns(signal);
    } catch (error) {
      LogService.info("Failed to pull promocode campaigns", { error });
    }
  }
  if (!signal?.aborted) {
    try {
      await fetchConfigPicture(config.id);
    } catch (error) {
      LogService.info("Failed to fetch config picture", { error });
    }
  }
  POSConfigModule.setExtraProgress(3);

  // pull available offers for this group
  if (!signal?.aborted) {
    try {
      await loadOffers();
      LogService.info("Offers object instantiated");
    } catch (error) {
      LogService.info("Failed to instantiate offers object", { error });
    }
  }
  POSConfigModule.setExtraProgress(4);

  // sync group cash locations
  POSConfigModule.setLoadingText("loading-finalizing");
  if (!signal?.aborted) {
    try {
      await syncGroupLocations();
    } catch (error) {
      LogService.info("Failed to sync group locations", { error });
    }
  }

  // The back office links a check-in area to POS config (APPS-510)
  if (!signal?.aborted) {
    try {
      await CheckInModule.loadConfig({
        groupId: config.group,
        posConfigId: config.id,
      });
    } catch (error) {
      LogService.info("Failed to fetch the check-in area", { error });
    }
  }
  POSConfigModule.setExtraProgress(5);

  return !signal?.aborted;
}
